import { useState, useEffect, useRef } from "react";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Placeholder from "@tiptap/extension-placeholder";
import FontFamily from "@tiptap/extension-font-family";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { CollapsibleSection, DrawingNode, FontSize, TableKeymap } from '@/lib/tiptapExtensions';
import InsertTableDialog from "./insert-table-dialog";
import { 
  Save, 
  Trash2, 
  X, 
  Tag, 
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link2,
  ImageIcon,
  Table as TableIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Highlighter,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronsUp,
  ChevronsDown,
  Copy,
  History,
  Upload,
  Download,
  FileText,
  FileJson,
  Paperclip,
  Mic,
  Play,
  Pause,
  StopCircle,
  Loader2,
  Plus,
  PenTool,
  Bot,
  User,
  MessageCircle,
  RefreshCw,
  Star,
  Check,
  Send,
  RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { uploadNoteImage } from "@/lib/note-image-upload";
import { exportToPDF, exportToMarkdown, exportToJSON } from "@/lib/exportUtils";
import type { JournalEntry, Goal, JournalEntryVersion, JournalAttachment, UserPreferences } from "@shared/schema";
import { isFutureGoal } from "@/lib/goal-visibility";

interface JournalEditorProps {
  entry: JournalEntry | null;
  isCreating: boolean;
  initialGoalId?: string | null;
  onSave: (data: { title: string; content: any; plainContent: string; tags: string[]; category?: string; goalId?: string | null }) => void;
  onDelete?: () => void;
  onCancel: () => void;
}

// Helper to convert Quill Delta to HTML for backward compatibility
const convertQuillDeltaToHTML = (delta: any): string => {
  if (typeof delta === 'string') {
    // Already HTML or plain text
    if (delta.startsWith('<')) return delta;
    return `<p>${delta}</p>`;
  }
  
  if (delta?.ops) {
    // Quill Delta format - handle block-level structure
    let html = '';
    let currentBlock = '';
    let currentBlockType = 'paragraph';
    let listItems: string[] = [];
    let inList = false;
    let listType = '';
    
    delta.ops.forEach((op: any, index: number) => {
      if (typeof op.insert === 'string') {
        let text = op.insert;
        
        // Apply inline formatting
        if (op.attributes) {
          if (op.attributes.bold) text = `<strong>${text}</strong>`;
          if (op.attributes.italic) text = `<em>${text}</em>`;
          if (op.attributes.underline) text = `<u>${text}</u>`;
          if (op.attributes.strike) text = `<s>${text}</s>`;
          if (op.attributes.code) text = `<code>${text}</code>`;
          if (op.attributes.link) text = `<a href="${op.attributes.link}">${text}</a>`;
          if (op.attributes.color) text = `<span style="color: ${op.attributes.color}">${text}</span>`;
          if (op.attributes.background) text = `<span style="background-color: ${op.attributes.background}">${text}</span>`;
        }
        
        // Handle line breaks and block types
        const lines = text.split('\n');
        lines.forEach((line: string, lineIndex: number) => {
          if (lineIndex > 0) {
            // Process previous block
            const blockAttrs = op.attributes || {};
            
            // Check for list
            if (blockAttrs.list) {
              if (!inList || listType !== blockAttrs.list) {
                if (inList) {
                  html += listType === 'bullet' ? '<ul>' : '<ol>';
                  listItems.forEach(item => html += `<li>${item}</li>`);
                  html += listType === 'bullet' ? '</ul>' : '</ol>';
                  listItems = [];
                }
                inList = true;
                listType = blockAttrs.list;
              }
              listItems.push(currentBlock);
              currentBlock = '';
            } else {
              // Close any open list
              if (inList) {
                html += listType === 'bullet' ? '<ul>' : '<ol>';
                listItems.forEach(item => html += `<li>${item}</li>`);
                html += listType === 'bullet' ? '</ul>' : '</ol>';
                listItems = [];
                inList = false;
                listType = '';
              }
              
              // Handle heading
              if (blockAttrs.header) {
                const level = blockAttrs.header;
                html += `<h${level}>${currentBlock}</h${level}>`;
              } else if (blockAttrs.blockquote) {
                html += `<blockquote>${currentBlock}</blockquote>`;
              } else if (blockAttrs['code-block']) {
                html += `<pre><code>${currentBlock}</code></pre>`;
              } else {
                html += currentBlock ? `<p>${currentBlock}</p>` : '<p><br></p>';
              }
              currentBlock = '';
            }
          }
          currentBlock += line;
        });
      } else if (op.insert?.image) {
        // Handle embedded images
        html += `<img src="${op.insert.image}" alt="" />`;
      }
    });
    
    // Process final block
    if (inList) {
      html += listType === 'bullet' ? '<ul>' : '<ol>';
      listItems.forEach(item => html += `<li>${item || '<br>'}</li>`);
      html += listType === 'bullet' ? '</ul>' : '</ol>';
    } else if (currentBlock) {
      html += `<p>${currentBlock}</p>`;
    }
    
    return html || '<p></p>';
  }
  
  return '<p></p>';
};

const extractPlainText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};


export default function JournalEditor({ entry, isCreating, initialGoalId, onSave, onDelete, onCancel }: JournalEditorProps) {
  const getInitialContent = () => {
    if (!entry) return "";
    
    // Prefer draftContent if it exists (user has unsaved/unpublished changes)
    const contentSource = entry.draftContent || entry.content;
    
    if (typeof contentSource === 'string') {
      // Check if it's HTML
      if (contentSource.startsWith('<')) return contentSource;
      
      // Check if it's JSON (Quill Delta)
      if (contentSource.startsWith('{') || contentSource.startsWith('[')) {
        try {
          const parsed = JSON.parse(contentSource);
          return convertQuillDeltaToHTML(parsed);
        } catch (e) {
          return contentSource;
        }
      }
      
      return contentSource;
    } 
    
    if (typeof contentSource === 'object' && contentSource !== null) {
      // Check if it's Quill Delta format
      if ('ops' in contentSource) {
        return convertQuillDeltaToHTML(contentSource);
      }
      // Check if it has text property
      if ('text' in contentSource) {
        return (contentSource as { text: string }).text;
      }
    }
    
    return entry?.plainContent || "";
  };

  const [title, setTitle] = useState(entry?.title || "");
  const [tags, setTags] = useState<string[]>(entry?.tags || []);
  const [category, setCategory] = useState(entry?.category || "Uncategorized");
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(entry?.goalId || initialGoalId || null);
  const [newTag, setNewTag] = useState("");
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const [lastEdited, setLastEdited] = useState(0);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasCollapsibleSections, setHasCollapsibleSections] = useState(false);
  
  // AI Research Chatbot state
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string; markedAsResult?: boolean; messageNumber?: number }>>([]);
  const [chatInput, setChatInput] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isChatComplete, setIsChatComplete] = useState(false);
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: goals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: categories = [] } = useQuery<{ id: string; name: string; color: string }[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: attachments = [] } = useQuery<JournalAttachment[]>({
    queryKey: ["/api/journal", entry?.id, "attachments"],
    enabled: !!entry?.id,
  });

  // User preferences for metadata collapsed state
  const { data: userPreferences } = useQuery<UserPreferences>({
    queryKey: ["/api/user/preferences"],
  });

  const [metadataCollapsed, setMetadataCollapsed] = useState(false);

  // Sync local state with database when preferences load
  useEffect(() => {
    if (userPreferences?.journalMetadataCollapsed === true || userPreferences?.journalMetadataCollapsed === false) {
      setMetadataCollapsed(userPreferences.journalMetadataCollapsed);
    }
  }, [userPreferences?.journalMetadataCollapsed]);

  const updatePreferencesMutation = useMutation({
    mutationFn: async (collapsed: boolean) => {
      const response = await fetch('/api/user/preferences', {
        method: 'PATCH',
        body: JSON.stringify({ journalMetadataCollapsed: collapsed }),
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to update preferences');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
    },
  });

  const toggleMetadataCollapsed = () => {
    const newState = !metadataCollapsed;
    setMetadataCollapsed(newState);
    updatePreferencesMutation.mutate(newState);
  };

  const createAttachmentMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`/api/journal/${entry?.id}/attachments`, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to create attachment');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal", entry?.id, "attachments"] });
    },
    onError: () => {
    }
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/journal/attachments/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete attachment');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal", entry?.id, "attachments"] });
    },
    onError: () => {
    }
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        // Disable built-in extensions we're configuring separately
        link: false,
        underline: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Highlight.configure({
        multicolor: true,
      }),
      Subscript,
      Superscript,
      Placeholder.configure({
        placeholder: 'Start writing your journal entry...',
      }),
      CollapsibleSection,
      DrawingNode,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
      TableKeymap,
    ],
    content: getInitialContent(),
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-xl focus:outline-none min-h-[400px] p-4 !max-w-none w-full',
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith('image/'));
        if (imageItem) {
          const file = imageItem.getAsFile();
          if (file) {
            event.preventDefault();
            // Upload to object storage (no base64 in journal content/database),
            // then insert the served URL at the current selection.
            void uploadNoteImage(file)
              .then((src) => {
                const { schema } = view.state;
                const node = schema.nodes.image.create({ src });
                view.dispatch(view.state.tr.replaceSelectionWith(node));
              })
              .catch((error: any) => {
                toast({
                  title: "Image Upload Failed",
                  description: error?.message || "Could not upload pasted image",
                  variant: "destructive",
                });
              });
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []);
        const imageFile = files.find((file) => file.type.startsWith('image/'));
        if (imageFile) {
          event.preventDefault();
          const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
          // Upload to object storage (no base64 in journal content/database),
          // then insert the served URL at the drop position.
          void uploadNoteImage(imageFile)
            .then((src) => {
              const { schema } = view.state;
              const node = schema.nodes.image.create({ src });
              view.dispatch(
                view.state.tr.insert(coordinates?.pos ?? view.state.selection.from, node),
              );
            })
            .catch((error: any) => {
              toast({
                title: "Image Upload Failed",
                description: error?.message || "Could not upload dropped image",
                variant: "destructive",
              });
            });
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: updatedEditor }) => {
      setLastEdited(Date.now());
      let hasCollapsibles = false;
      updatedEditor.state.doc.descendants((node: any) => {
        if (node.type.name === 'collapsibleSection') {
          hasCollapsibles = true;
          return false;
        }
        return undefined;
      });
      setHasCollapsibleSections(hasCollapsibles);
    },
    onCreate: ({ editor: createdEditor }) => {
      let hasCollapsibles = false;
      createdEditor.state.doc.descendants((node: any) => {
        if (node.type.name === 'collapsibleSection') {
          hasCollapsibles = true;
          return false;
        }
        return undefined;
      });
      setHasCollapsibleSections(hasCollapsibles);
    },
  });

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  // Auto-create for new journal entries
  useEffect(() => {
    if (!isCreating || !editor || !title.trim() || lastEdited === 0) return;
    const timeoutId = setTimeout(() => {
      const html = editor.getHTML();
      const plainText = extractPlainText(html);
      onSave({ title, content: html, plainContent: plainText, tags, category, goalId: selectedGoalId });
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [lastEdited, title, isCreating]);

  useEffect(() => {
    if (!entry || isCreating || !editor) return;

    const timeoutId = setTimeout(async () => {
      const html = editor.getHTML();
      const plainText = extractPlainText(html);
      
      if (html && (html !== entry.content || title !== entry.title)) {
        setAutoSaveStatus('saving');
        try {
          const response = await fetch(`/api/journal/${entry.id}/draft`, {
            method: 'PATCH',
            body: JSON.stringify({ 
              status: 'draft',
              draftContent: html,
              plainContent: plainText,
              lastAutoSavedAt: new Date().toISOString()
            }),
            headers: { 'Content-Type': 'application/json' }
          });
          if (!response.ok) throw new Error('Auto-save failed');
          setAutoSaveStatus('saved');
        } catch (error) {
          setAutoSaveStatus('unsaved');
          console.error('Auto-save failed:', error);
        }
      }
    }, 3000);

    return () => clearTimeout(timeoutId);
  }, [editor?.getHTML(), title, entry, isCreating]);

  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/journal/${id}/duplicate`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to duplicate entry');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      onCancel();
    },
    onError: () => {
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/journal/${id}/publish`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to publish draft');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      onCancel();
    },
    onError: () => {
    },
  });

  const publishVersionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(`/api/journal/${id}/publish-version`, {
        method: 'POST',
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to publish version');
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Version Published",
        description: "A new version snapshot has been created in your history.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/journal', entry?.id, 'versions'] });
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to publish version",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const { data: versions = [] } = useQuery<JournalEntryVersion[]>({
    queryKey: ['/api/journal', entry?.id, 'versions'],
    enabled: !!entry && !isCreating && showVersionHistory,
  });

  const restoreMutation = useMutation({
    mutationFn: async ({ entryId, versionId }: { entryId: string; versionId: string }) => {
      const response = await fetch(`/api/journal/${entryId}/versions/${versionId}/restore`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Failed to restore version');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      setShowVersionHistory(false);
      onCancel();
    },
    onError: () => {
    },
  });

  const saveDraftMutation = useMutation({
    mutationFn: async () => {
      if (!entry || !editor) throw new Error('No entry to save');
      const html = editor.getHTML();
      const plainText = extractPlainText(html);
      const response = await fetch(`/api/journal/${entry.id}/draft`, {
        method: 'PATCH',
        body: JSON.stringify({ 
          status: 'draft',
          draftContent: html,
          plainContent: plainText,
          lastAutoSavedAt: new Date().toISOString()
        }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to save');
      return response.json();
    },
    onSuccess: () => {
      setAutoSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ["/api/journal"] });
      toast({
        title: "Saved",
        description: "Your changes have been saved.",
      });
    },
    onError: () => {
      setAutoSaveStatus('unsaved');
      toast({
        title: "Save Failed",
        description: "Could not save your changes. Please try again.",
        variant: "destructive",
      });
    },
  });

  const saveEntryMutation = useMutation({
    mutationFn: async () => {
      if (!entry || !editor) throw new Error('No entry to save');
      const html = editor.getHTML();
      const plainText = extractPlainText(html);
      const response = await fetch(`/api/journal/${entry.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title, content: html, plainContent: plainText }),
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to save entry');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/journal'] });
      setAutoSaveStatus('saved');
      toast({ title: 'Saved', description: 'Entry saved successfully.' });
    },
    onError: () => {
      toast({ title: 'Save Failed', description: 'Could not save entry.', variant: 'destructive' });
    },
  });

  // AI Research mutation
  const generateResearchMutation = useMutation({
    mutationFn: async ({ question, conversationHistory }: { question: string; conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }> }) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      
      try {
        const response = await fetch('/api/notes/research', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question, conversationHistory }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) throw new Error('Failed to generate research');
        return response.json();
      } catch (error: any) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          throw new Error('Research generation timed out. Please try a simpler question.');
        }
        throw error;
      }
    },
    onSuccess: (data: { answer: string }) => {
      if (!data.answer || data.answer.trim() === "") {
        return;
      }
      setChatMessages(prev => {
        const assistantCount = prev.filter(m => m.role === 'assistant').length + 1;
        return [...prev, { 
          role: 'assistant', 
          content: data.answer,
          messageNumber: assistantCount,
          markedAsResult: false
        }];
      });
    },
    onError: (error: any) => {
      toast({
        title: "Research Failed",
        description: error?.message || "Failed to generate research. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    const userMsgCount = chatMessages.filter(m => m.role === 'user').length + 1;
    const updatedMessages = [...chatMessages, { 
      role: 'user' as const, 
      content: userMessage,
      messageNumber: userMsgCount 
    }];
    setChatMessages(updatedMessages);
    setChatInput("");
    setIsChatComplete(false);
    generateResearchMutation.mutate({ 
      question: userMessage, 
      conversationHistory: updatedMessages.slice(0, -1)
    });
  };

  const toggleMarkAsResult = (index: number) => {
    setChatMessages(prev => prev.map((msg, i) => 
      i === index ? { ...msg, markedAsResult: !msg.markedAsResult } : msg
    ));
  };

  const markedResults = chatMessages.filter(m => m.markedAsResult);

  const escapeHtml = (text: string): string => {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const handlePostChatToJournal = (resultsOnly: boolean = false) => {
    if (!editor || chatMessages.length === 0) return;
    
    const messagesToPost = resultsOnly 
      ? chatMessages.filter(m => m.markedAsResult)
      : chatMessages;
    
    if (messagesToPost.length === 0) {
      toast({
        title: "No content to post",
        description: resultsOnly 
          ? "Please mark at least one response as a result first."
          : "No messages to post.",
        variant: "destructive",
      });
      return;
    }
    
    const existingHtml = editor.getHTML();
    const hasExistingContent = existingHtml && existingHtml !== '<p></p>' && existingHtml.trim() !== '';
    
    const hasSeparator = existingHtml.includes('--- AI Research Results ---') || existingHtml.includes('--- AI Research Conversation ---');
    
    let newHtml = '';
    const separatorText = resultsOnly ? '--- AI Research Results ---' : '--- AI Research Conversation ---';
    
    if (hasExistingContent && !hasSeparator) {
      newHtml += '<p></p><p><strong>' + separatorText + '</strong></p><p></p>';
    } else if (hasExistingContent && hasSeparator) {
      newHtml += '<p></p><p><em>' + (resultsOnly ? '(additional results)' : '(continued conversation)') + '</em></p><p></p>';
    }
    
    messagesToPost.forEach((msg) => {
      newHtml += '<p><strong>' + (msg.role === 'user' ? 'You: ' : 'AI Assistant: ') + '</strong></p>';
      const paragraphs = msg.content.split('\n\n').filter(p => p.trim());
      paragraphs.forEach(paragraph => {
        newHtml += '<p>' + escapeHtml(paragraph.trim()) + '</p>';
      });
      newHtml += '<p></p>';
    });
    
    const combinedHtml = hasExistingContent ? existingHtml + newHtml : newHtml;
    editor.commands.setContent(combinedHtml);
    setIsChatComplete(true);
    
    const itemType = resultsOnly ? "results" : "conversation";
    toast({
      title: "Posted to Journal Entry",
      description: hasExistingContent 
        ? `AI ${itemType} has been appended to your journal entry.`
        : `AI ${itemType} has been added to your journal entry.`,
    });
  };

  const handleClearChat = () => {
    setChatMessages([]);
    setChatInput("");
    setIsChatComplete(false);
  };

  const handleSubmit = () => {
    if (editor) {
      const html = editor.getHTML();
      const plainText = extractPlainText(html);

      onSave({
        title,
        content: html,
        plainContent: plainText,
        tags,
        category,
        goalId: selectedGoalId,
      });
    }
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const setLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor?.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor?.chain().focus().setImage({ src: url }).run();
    }
  };

  const handleInsertTable = (rows: number, cols: number) => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
  };


  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Upload to object storage and register a media record — journals never
      // embed base64 into content/database.
      const src = await uploadNoteImage(file);
      editor?.chain().focus().setImage({ src }).run();
    } catch (error: any) {
      toast({
        title: "Image Upload Failed",
        description: error?.message || "Could not upload image",
        variant: "destructive",
      });
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      return;
    }
    
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      
      await createAttachmentMutation.mutateAsync({
        type: 'file',
        name: file.name,
        data: base64,
        mimeType: file.type,
        size: file.size
      });
    };
    reader.readAsDataURL(file);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      
      const interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      (mediaRecorder as any).intervalId = interval;
      (window as any).currentRecorder = mediaRecorder;
    } catch (error) {
    }
  };

  const stopRecording = () => {
    const recorder = (window as any).currentRecorder;
    if (recorder) {
      clearInterval((recorder as any).intervalId);
      recorder.stop();
      recorder.stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      setIsRecording(false);
    }
  };

  const saveVoiceNote = async () => {
    if (!audioBlob) return;
    
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      await createAttachmentMutation.mutateAsync({
        type: 'voice',
        name: `Voice Note ${new Date().toLocaleString()}`,
        data: base64,
        mimeType: 'audio/webm',
        size: audioBlob.size
      });
      setShowVoiceRecorder(false);
      setAudioBlob(null);
      setRecordingTime(0);
    };
    reader.readAsDataURL(audioBlob);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const downloadFile = (attachment: JournalAttachment) => {
    const href = attachment.data || attachment.url || '';
    // Block dangerous URL schemes (e.g. javascript:, data: non-blob, vbscript:)
    const normalized = href.trim().toLowerCase();
    if (!normalized.startsWith('https://') && !normalized.startsWith('http://') && !normalized.startsWith('blob:') && !normalized.startsWith('data:')) {
      console.warn('Blocked download of unsafe URL scheme');
      return;
    }
    const link = document.createElement('a');
    link.href = href;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Fixed Header with Action Buttons */}
      <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-2 pb-4 min-w-0">
        <div className="flex items-center gap-4 min-w-0 flex-shrink-0">
          <h2 className="text-2xl font-bold whitespace-nowrap">
            {isCreating ? "Create New Entry" : "Edit Entry"}
          </h2>
          {!isCreating && (
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {autoSaveStatus === 'saving' && '💾 Saving...'}
              {autoSaveStatus === 'saved' && '✓ Saved'}
              {autoSaveStatus === 'unsaved' && '⚠ Unsaved changes'}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 min-w-0">
          <Button variant="outline" onClick={onCancel} data-testid="button-cancel">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          {!isCreating && entry && (
            <>
              <Button
                onClick={() => saveEntryMutation.mutate()}
                disabled={saveEntryMutation.isPending}
                data-testid="button-save-entry"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={() => duplicateMutation.mutate(entry.id)} data-testid="button-duplicate">
                <Copy className="w-4 h-4 mr-2" />
                Duplicate
              </Button>
              <Button variant="outline" onClick={() => setShowVersionHistory(!showVersionHistory)} data-testid="button-history">
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
              <Button 
                variant="default"
                onClick={() => publishVersionMutation.mutate(entry.id)}
                disabled={autoSaveStatus === 'unsaved' || publishVersionMutation.isPending}
                data-testid="button-publish-version"
              >
                {publishVersionMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Publish Version
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="button-export">
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={async () => {
                    try {
                      await exportToPDF(entry);
                    } catch (error) {
                    }
                  }} data-testid="menu-export-pdf">
                    <FileText className="w-4 h-4 mr-2" />
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportToMarkdown(entry)} data-testid="menu-export-markdown">
                    <FileText className="w-4 h-4 mr-2" />
                    Export as Markdown
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportToJSON(entry)} data-testid="menu-export-json">
                    <FileJson className="w-4 h-4 mr-2" />
                    Export as JSON
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {entry.draftContent && (
                <Button onClick={() => publishMutation.mutate(entry.id)} data-testid="button-publish">
                  <Upload className="w-4 h-4 mr-2" />
                  Publish Draft
                </Button>
              )}
            </>
          )}
          {onDelete && (
            <Button variant="destructive" onClick={onDelete} data-testid="button-delete">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Collapsible Metadata Section */}
      <div className="flex-shrink-0 border rounded-lg bg-primary/5 mb-4">
        <div 
          className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-primary/10 transition-colors"
          onClick={toggleMetadataCollapsed}
          data-testid="button-toggle-metadata"
        >
          <span className="text-sm font-medium text-muted-foreground">
            {metadataCollapsed ? 'Show Details' : 'Entry Details'}
          </span>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {metadataCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        {!metadataCollapsed && (
          <div className="px-4 pb-4 space-y-4">
            <Input
              placeholder="Entry title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-lg font-semibold"
              data-testid="input-title"
            />

            {/* Category */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="select-entry-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                  <CategorySelectGroups categories={categories} />
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Linked Goal</label>
              <Select value={selectedGoalId || "none"} onValueChange={(val) => setSelectedGoalId(val === "none" ? null : val)}>
                <SelectTrigger data-testid="select-entry-goal">
                  <SelectValue placeholder="Select a goal (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No linked goal</SelectItem>
                  {goals.filter(g => g.status === "active" && !isFutureGoal(g) && g.progress < 100).map((goal) => (
                    <SelectItem key={goal.id} value={goal.id}>
                      {goal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Add a tag..."
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTag()}
                  data-testid="input-tag"
                />
                <Button onClick={addTag} variant="outline" data-testid="button-add-tag">
                  <Tag className="w-4 h-4" />
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)} data-testid={`badge-tag-${tag}`}>
                      {tag}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Toolbar - Always visible */}
      <div className="flex-shrink-0 mb-4">
        <Card>
          <CardContent className="p-2">
            <div className="flex flex-wrap gap-1">
              {/* Text Formatting */}
              <Button
                variant={editor.isActive('bold') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleBold().run()}
                title="Bold"
                data-testid="button-bold"
              >
                <Bold className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('italic') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                title="Italic"
                data-testid="button-italic"
              >
                <Italic className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('underline') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                title="Underline"
                data-testid="button-underline"
              >
                <UnderlineIcon className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('strike') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleStrike().run()}
                title="Strikethrough"
                data-testid="button-strike"
              >
                <Strikethrough className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('code') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleCode().run()}
                title="Inline Code"
                data-testid="button-code"
              >
                <Code className="w-4 h-4" />
              </Button>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Font Family */}
              <Select
                value={editor.getAttributes('textStyle').fontFamily || 'default'}
                onValueChange={(value) => {
                  if (value === 'default') {
                    editor.chain().focus().unsetFontFamily().run();
                  } else {
                    editor.chain().focus().setFontFamily(value).run();
                  }
                }}
              >
                <SelectTrigger className="w-[120px] h-8" title="Font Family" data-testid="select-font-family">
                  <SelectValue placeholder="Font" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="Arial" style={{ fontFamily: 'Arial' }}>Arial</SelectItem>
                  <SelectItem value="Georgia" style={{ fontFamily: 'Georgia' }}>Georgia</SelectItem>
                  <SelectItem value="Times New Roman" style={{ fontFamily: 'Times New Roman' }}>Times New Roman</SelectItem>
                  <SelectItem value="Courier New" style={{ fontFamily: 'Courier New' }}>Courier New</SelectItem>
                  <SelectItem value="Verdana" style={{ fontFamily: 'Verdana' }}>Verdana</SelectItem>
                  <SelectItem value="Trebuchet MS" style={{ fontFamily: 'Trebuchet MS' }}>Trebuchet MS</SelectItem>
                  <SelectItem value="Comic Sans MS" style={{ fontFamily: 'Comic Sans MS' }}>Comic Sans MS</SelectItem>
                  <SelectItem value="Impact" style={{ fontFamily: 'Impact' }}>Impact</SelectItem>
                </SelectContent>
              </Select>

              {/* Font Size */}
              <Select
                value={editor.getAttributes('textStyle').fontSize || 'default'}
                onValueChange={(value) => {
                  if (value === 'default') {
                    (editor.chain().focus() as any).unsetFontSize().run();
                  } else {
                    (editor.chain().focus() as any).setFontSize(value).run();
                  }
                }}
              >
                <SelectTrigger className="w-[80px] h-8" title="Font Size" data-testid="select-font-size">
                  <SelectValue placeholder="Size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default</SelectItem>
                  <SelectItem value="10px">10</SelectItem>
                  <SelectItem value="12px">12</SelectItem>
                  <SelectItem value="14px">14</SelectItem>
                  <SelectItem value="16px">16</SelectItem>
                  <SelectItem value="18px">18</SelectItem>
                  <SelectItem value="20px">20</SelectItem>
                  <SelectItem value="24px">24</SelectItem>
                  <SelectItem value="28px">28</SelectItem>
                  <SelectItem value="32px">32</SelectItem>
                  <SelectItem value="36px">36</SelectItem>
                  <SelectItem value="48px">48</SelectItem>
                  <SelectItem value="64px">64</SelectItem>
                </SelectContent>
              </Select>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Headings */}
              <Button
                variant={editor.isActive('heading', { level: 1 }) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                title="Heading 1"
                data-testid="button-h1"
              >
                <Heading1 className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('heading', { level: 2 }) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                title="Heading 2"
                data-testid="button-h2"
              >
                <Heading2 className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('heading', { level: 3 }) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                title="Heading 3"
                data-testid="button-h3"
              >
                <Heading3 className="w-4 h-4" />
              </Button>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Lists */}
              <Button
                variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleBulletList().run()}
                title="Bullet List"
                data-testid="button-bullet-list"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleOrderedList().run()}
                title="Numbered List"
                data-testid="button-ordered-list"
              >
                <ListOrdered className="w-4 h-4" />
              </Button>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Collapsible Section */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  editor.chain().focus().insertCollapsible().run();
                }}
                className={editor.isActive('collapsibleSection') ? 'bg-accent' : ''}
                title="Insert Collapsible Section"
                data-testid="button-insert-collapsible"
              >
                <ChevronDown className="w-4 h-4" />
              </Button>
              
              {/* Expand/Collapse All - only show when there are collapsible sections */}
              {hasCollapsibleSections && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().expandAllCollapsibles().run()}
                    title="Expand All Sections"
                    data-testid="button-expand-all"
                  >
                    <ChevronsDown className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => editor.chain().focus().collapseAllCollapsibles().run()}
                    title="Collapse All Sections"
                    data-testid="button-collapse-all"
                  >
                    <ChevronsUp className="w-4 h-4" />
                  </Button>
                </>
              )}

              {/* Drawing Canvas */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  editor.chain().focus().insertDrawing().run();
                }}
                className={editor.isActive('drawingNode') ? 'bg-accent' : ''}
                title="Insert Drawing Canvas"
                data-testid="button-insert-drawing"
              >
                <PenTool className="w-4 h-4" />
              </Button>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Alignment */}
              <Button
                variant={editor.isActive({ textAlign: 'left' }) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().setTextAlign('left').run()}
                title="Align Left"
                data-testid="button-align-left"
              >
                <AlignLeft className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive({ textAlign: 'center' }) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().setTextAlign('center').run()}
                title="Align Center"
                data-testid="button-align-center"
              >
                <AlignCenter className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive({ textAlign: 'right' }) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().setTextAlign('right').run()}
                title="Align Right"
                data-testid="button-align-right"
              >
                <AlignRight className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive({ textAlign: 'justify' }) ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                title="Justify"
                data-testid="button-align-justify"
              >
                <AlignJustify className="w-4 h-4" />
              </Button>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Colors */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" title="Text Color" data-testid="button-text-color">
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: editor.getAttributes('textStyle').color || '#000' }} />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2">
                  <div className="grid grid-cols-5 gap-1">
                    {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080', '#008000'].map(color => (
                      <button
                        key={color}
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: color }}
                        onClick={() => editor.chain().focus().setColor(color).run()}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" title="Highlight" data-testid="button-highlight">
                    <Highlighter className="w-4 h-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2">
                  <div className="grid grid-cols-5 gap-1">
                    {['#FFFF00', '#00FF00', '#00FFFF', '#FF00FF', '#FFA500', '#FF0000', '#0000FF', '#800080'].map(color => (
                      <button
                        key={color}
                        className="w-6 h-6 rounded border"
                        style={{ backgroundColor: color }}
                        onClick={() => editor.chain().focus().toggleHighlight({ color }).run()}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Other formatting */}
              <Button
                variant={editor.isActive('blockquote') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleBlockquote().run()}
                title="Quote"
                data-testid="button-blockquote"
              >
                <Quote className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('subscript') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleSubscript().run()}
                title="Subscript"
                data-testid="button-subscript"
              >
                <span className="text-xs">X₂</span>
              </Button>
              <Button
                variant={editor.isActive('superscript') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => editor.chain().focus().toggleSuperscript().run()}
                title="Superscript"
                data-testid="button-superscript"
              >
                <span className="text-xs">X²</span>
              </Button>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Media & Links */}
              <Button
                variant="ghost"
                size="sm"
                onClick={setLink}
                title="Insert Link"
                data-testid="button-link"
              >
                <Link2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={addImage}
                title="Insert Image URL"
                data-testid="button-image"
              >
                <ImageIcon className="w-4 h-4" />
              </Button>
              <Button
                variant={editor.isActive('table') ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setShowTableDialog(true)}
                title="Insert Table"
                data-testid="button-insert-table"
              >
                <TableIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => imageInputRef.current?.click()}
                title="Upload Image"
                data-testid="button-upload-image"
              >
                <Upload className="w-4 h-4" />
              </Button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                title="Attach File"
                data-testid="button-attach-file"
              >
                <Paperclip className="w-4 h-4" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowVoiceRecorder(true)}
                title="Record Voice Note"
                data-testid="button-record-voice"
              >
                <Mic className="w-4 h-4" />
              </Button>

              <Separator orientation="vertical" className="h-8 mx-1" />

              {/* Undo/Redo */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editor.chain().focus().undo().run()}
                disabled={!editor.can().undo()}
                title="Undo"
                data-testid="button-undo"
              >
                <Undo className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => editor.chain().focus().redo().run()}
                disabled={!editor.can().redo()}
                title="Redo"
                data-testid="button-redo"
              >
                <Redo className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>

      {/* Scrollable Content Area - Editor and Attachments */}
      <div className="flex-1 w-full overflow-y-auto space-y-4">
        {/* Editor */}
        <Card className="w-full">
          <CardContent className="p-0 w-full">
            <EditorContent editor={editor} className="tiptap-editor w-full" />
          </CardContent>
        </Card>

        {/* AI Research Chatbot Section */}
        <div className="border-2 border-blue-400 rounded-lg bg-blue-50/50 dark:bg-blue-950/20">
          <button
            onClick={() => setIsChatOpen(!isChatOpen)}
            className="w-full flex items-center justify-between p-3 hover:bg-blue-100/50 dark:hover:bg-blue-900/30 transition-colors"
          >
            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
              <MessageCircle className="h-4 w-4" />
              <span className="text-sm font-medium">AI Research Assistant</span>
              {chatMessages.length > 0 && (
                <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  {chatMessages.length} message{chatMessages.length !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            {isChatOpen ? <ChevronUp className="h-4 w-4 text-blue-600 dark:text-blue-400" /> : <ChevronDown className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
          </button>
          
          {isChatOpen && (
            <div className="p-3 border-t border-blue-200 dark:border-blue-800 space-y-3">
              {/* Clear Button */}
              {chatMessages.length > 0 && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearChat}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Clear Chat
                  </Button>
                </div>
              )}

              {/* Chat Messages */}
              {chatMessages.length > 0 && (
                <div className="max-h-[350px] overflow-y-auto space-y-3 p-2 bg-white dark:bg-background rounded-md border">
                  {chatMessages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex flex-col items-center gap-1">
                          <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                            msg.markedAsResult 
                              ? 'bg-green-100 dark:bg-green-900 ring-2 ring-green-500' 
                              : 'bg-blue-100 dark:bg-blue-900'
                          }`}>
                            <Bot className={`h-4 w-4 ${
                              msg.markedAsResult 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-blue-600 dark:text-blue-400'
                            }`} />
                          </div>
                          {msg.messageNumber && (
                            <span className="text-[10px] text-muted-foreground font-mono">#{msg.messageNumber}</span>
                          )}
                        </div>
                      )}
                      <div className="flex flex-col gap-1 max-w-[80%]">
                        <div
                          className={`rounded-lg p-3 text-sm ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : msg.markedAsResult
                                ? 'bg-green-50 dark:bg-green-950 border-2 border-green-400'
                                : 'bg-muted'
                          }`}
                        >
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                        </div>
                        {msg.role === 'assistant' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleMarkAsResult(index)}
                            className={`self-start h-6 px-2 text-xs ${
                              msg.markedAsResult 
                                ? 'text-green-600 hover:text-green-700 bg-green-50 dark:bg-green-950' 
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {msg.markedAsResult ? (
                              <>
                                <Check className="h-3 w-3 mr-1" />
                                Marked as Result
                              </>
                            ) : (
                              <>
                                <Star className="h-3 w-3 mr-1" />
                                Mark as Result
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      {msg.role === 'user' && (
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-primary" />
                          </div>
                          {msg.messageNumber && (
                            <span className="text-[10px] text-muted-foreground font-mono">Q{msg.messageNumber}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {generateResearchMutation.isPending && (
                    <div className="flex gap-2 justify-start">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <Bot className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="bg-muted rounded-lg p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
                          <span className="text-muted-foreground">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Chat Input */}
              <div className="flex gap-2">
                <Textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={chatMessages.length === 0 
                    ? "Ask your research question... (e.g., 'What are the benefits of meditation?')"
                    : "Ask a follow-up question..."
                  }
                  className="text-sm min-h-[50px] resize-none overflow-hidden flex-1"
                  style={{ height: 'auto' }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = `${Math.max(50, target.scrollHeight)}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendChatMessage();
                    }
                  }}
                  data-testid="input-journal-chat-message"
                />
                <Button
                  onClick={handleSendChatMessage}
                  disabled={generateResearchMutation.isPending || !chatInput.trim()}
                  size="icon"
                  className="h-[50px] w-[50px] shrink-0"
                  data-testid="button-send-journal-chat"
                >
                  {generateResearchMutation.isPending ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Post Results Section */}
              {chatMessages.length > 0 && chatMessages.some(m => m.role === 'assistant') && (
                <div className="border-2 border-red-400 rounded-lg p-3 bg-red-50/50 dark:bg-red-950/20 space-y-3">
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm font-medium">Save to Journal Entry</span>
                    {markedResults.length > 0 && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                        {markedResults.length} result{markedResults.length !== 1 ? 's' : ''} marked
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      onClick={() => handlePostChatToJournal(true)}
                      size="sm"
                      disabled={markedResults.length === 0}
                      className={markedResults.length > 0 
                        ? "bg-green-600 hover:bg-green-700 text-white flex-1" 
                        : "flex-1"
                      }
                      data-testid="button-post-results-journal"
                    >
                      <Star className="h-4 w-4 mr-1" />
                      Post Results Only ({markedResults.length})
                    </Button>
                    
                    <Button
                      onClick={() => handlePostChatToJournal(false)}
                      size="sm"
                      variant="outline"
                      className="flex-1 border-red-400 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                      data-testid="button-post-full-chat-journal"
                    >
                      <MessageCircle className="h-4 w-4 mr-1" />
                      Post Full Conversation
                    </Button>
                  </div>
                  
                  {markedResults.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Tip: Click "Mark as Result" on any AI response to select specific results to post.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Attachments Display */}
        {attachments.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <h3 className="text-sm font-semibold mb-2">Attachments</h3>
            <div className="space-y-2">
              {attachments.map(att => (
                <div key={att.id} className="flex items-center justify-between p-2 bg-muted rounded" data-testid={`attachment-${att.id}`}>
                  <div className="flex items-center gap-2">
                    {att.type === 'voice' ? <Mic className="h-4 w-4" /> : <Paperclip className="h-4 w-4" />}
                    <span className="text-sm">{att.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {(att.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {att.type === 'voice' && att.data && (
                      <audio src={att.data} controls className="h-8" />
                    )}
                    {att.type === 'file' && (
                      <Button size="sm" variant="ghost" onClick={() => downloadFile(att)} data-testid={`button-download-${att.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => deleteAttachmentMutation.mutate(att.id)}
                      data-testid={`button-delete-${att.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Version History Dialog */}
      <Dialog open={showVersionHistory} onOpenChange={setShowVersionHistory}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Version History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {versions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No version history available</p>
                <p className="text-sm mt-2">Versions are created when you publish drafts</p>
              </div>
            ) : (
              <div className="space-y-3">
                {versions.map((version: JournalEntryVersion) => (
                  <Card key={version.id} className="hover:bg-accent/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">Version {version.versionNumber}</Badge>
                            <span className="text-sm text-muted-foreground">
                              {new Date(version.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <h4 className="font-semibold">{version.title}</h4>
                          <div className="text-sm text-muted-foreground line-clamp-2">
                            {typeof version.content === 'string' 
                              ? extractPlainText(version.content)
                              : JSON.stringify(version.content)}
                          </div>
                          {version.tags && version.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {version.tags.map(tag => (
                                <Badge key={tag} variant="secondary" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (confirm(`Restore to version ${version.versionNumber}? This will replace the current content.`)) {
                              restoreMutation.mutate({ 
                                entryId: entry!.id, 
                                versionId: version.id 
                              });
                            }
                          }}
                          disabled={restoreMutation.isPending}
                          data-testid={`button-restore-version-${version.versionNumber}`}
                        >
                          Restore
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Recorder Dialog */}
      <Dialog open={showVoiceRecorder} onOpenChange={setShowVoiceRecorder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Voice Note</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="text-2xl font-mono">{formatTime(recordingTime)}</div>
            <div className="flex gap-2">
              {!isRecording ? (
                <Button onClick={startRecording} data-testid="button-start-recording">
                  <Mic className="h-4 w-4 mr-2" /> Start Recording
                </Button>
              ) : (
                <Button onClick={stopRecording} variant="destructive" data-testid="button-stop-recording">
                  <StopCircle className="h-4 w-4 mr-2" /> Stop
                </Button>
              )}
            </div>
            {audioBlob && (
              <div className="w-full">
                <audio src={URL.createObjectURL(audioBlob)} controls className="w-full" data-testid="audio-preview" />
                <Button onClick={saveVoiceNote} className="w-full mt-2" data-testid="button-save-voice">
                  Save Voice Note
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <InsertTableDialog
        open={showTableDialog}
        onOpenChange={setShowTableDialog}
        onInsert={handleInsertTable}
      />
    </div>
  );
}
